import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Award, PlayCircle, Video, MapPin } from "lucide-react";
import { api, errMsg, downloadBlob, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/pages/OrderPage";
import { ChangePasswordForm, usePwText } from "@/components/PasswordAuth";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import WarrantyView from "@/components/WarrantyView";
import PdfPreview from "@/components/PdfPreview";

const Empty = () => <p className="text-[#6E6259] py-10">—</p>;
const useList = (url) => {
  const [d, setD] = useState(null);
  const load = () => api.get(url).then((r) => setD(r.data)).catch(() => setD([]));
  useEffect(() => { load(); }, [url]); // eslint-disable-line
  return [d, load];
};

function Orders() {
  const { fmt } = useApp();
  const [orders] = useList("/me/orders");
  if (!orders) return null;
  if (!orders.length) return <Empty />;
  return (
    <div className="divide-y divide-[#E6DFD5] border-y border-[#E6DFD5]">
      {orders.map((o) => (
        <Link key={o.order_id} to={`/order/${o.order_id}`} className="py-5 flex flex-wrap items-center gap-4 justify-between hover:bg-white px-2 transition-colors" data-testid={`account-order-${o.order_id}`}>
          <div><p className="font-mono text-sm">{o.order_id}</p><p className="text-xs text-[#6E6259]">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} items</p></div>
          <StatusBadge s={o.status} /><span>{fmt(o.total)}</span>
        </Link>
      ))}
    </div>
  );
}

function Downloads() {
  const [rows, load] = useList("/me/downloads");
  const get = async (d) => {
    try {
      const r = await api.post(`/me/downloads/${d.download_id}/link`);
      await downloadBlob(r.data.url.replace("/api", ""), d.file_name);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows) return null;
  if (!rows.length) return <Empty />;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {rows.map((d) => (
        <div key={d.download_id} className="bg-white border border-[#E6DFD5] p-5 flex items-center justify-between gap-4" data-testid={`download-row-${d.download_id}`}>
          <div><p className="font-medium">{d.product_name}</p><p className="text-xs font-mono text-[#6E6259]">{d.format} · {d.file_name} · {d.count}/{d.limit}</p></div>
          <button disabled={d.count >= d.limit} onClick={() => get(d)} data-testid={`download-btn-${d.download_id}`} className="bg-[#3B220C] text-white p-3 disabled:opacity-30" aria-label="Download"><Download size={16} /></button>
        </div>
      ))}
    </div>
  );
}

function Courses() {
  const [rows] = useList("/me/enrollments");
  if (!rows) return null;
  if (!rows.length) return <Empty />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {rows.map((e) => {
        const pct = e.total_lessons ? Math.round((100 * e.completed_lessons.length) / e.total_lessons) : 0;
        return (
          <Link key={e.product_id} to={`/learn/${e.product_id}`} className="bg-white border border-[#E6DFD5] group" data-testid={`course-card-${e.product_id}`}>
            <div className="aspect-video overflow-hidden relative"><img src={fileUrl(e.image)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /><PlayCircle className="absolute inset-0 m-auto text-white" size={44} strokeWidth={1} /></div>
            <div className="p-5 space-y-3"><p className="font-serif text-xl">{e.product_name}</p><Progress value={pct} className="h-1" /><p className="text-xs text-[#6E6259]">{pct}%</p></div>
          </Link>
        );
      })}
    </div>
  );
}

function Workshops() {
  const [rows] = useList("/me/bookings");
  if (!rows) return null;
  if (!rows.length) return <Empty />;
  return (
    <div className="space-y-3">
      {rows.map((b) => (
        <div key={b.booking_id} className="bg-white border border-[#E6DFD5] p-5 flex flex-wrap justify-between gap-4" data-testid={`booking-row-${b.booking_id}`}>
          <div>
            <p className="font-medium">{b.product_name} · {b.seats} seat(s)</p>
            <p className="text-sm text-[#6E6259]">{new Date(b.session_start).toLocaleString()}</p>
            {b.mode === "zoom" && <p className="text-sm flex items-center gap-2 mt-1"><Video size={14} />{b.meeting_link?.startsWith("http") ? <a href={b.meeting_link} target="_blank" rel="noreferrer" className="underline">Join Zoom</a> : "Link coming soon"}</p>}
            {b.mode === "inperson" && <p className="text-sm flex items-center gap-2 mt-1"><MapPin size={14} />{b.venue}</p>}
          </div>
          {b.attended && <span className="text-xs font-mono text-[#2E6B40]">ATTENDED</span>}
        </div>
      ))}
    </div>
  );
}

function Warranties() {
  const [rows] = useList("/me/warranties");
  if (!rows) return null;
  return (
    <div className="space-y-4" data-testid="account-warranties">
      <Link to="/warranty" className="text-sm underline" data-testid="account-check-warranty-link">Check a warranty by code / QR</Link>
      {!rows.length ? <Empty /> : rows.map((w) => <WarrantyView key={w.serial} w={w} showQr={false} />)}
    </div>
  );
}

function Certificates() {
  const [rows] = useList("/me/certificates");
  if (!rows) return null;
  if (!rows.length) return <Empty />;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {rows.map((c) => (
        <div key={c.certificate_id} className="bg-white border border-[#E6DFD5] p-5 flex items-center gap-4">
          <Award className="text-[#C47A37]" size={28} />
          <div className="flex-1"><p className="font-medium">{c.title}</p><p className="text-xs font-mono text-[#6E6259]">{c.certificate_id}</p></div>
          <PdfPreview path={`/certificates/${c.certificate_id}/pdf`} filename={`${c.certificate_id}.pdf`} label="Preview" testid={`certificate-download-${c.certificate_id}`} className="!px-3" />
        </div>
      ))}
    </div>
  );
}

function Coupons() {
  const { user, setUser } = useApp();
  const [rows] = useList("/me/coupons");
  const [phone, setPhone] = useState(user.phone || "");
  const { lang } = useApp();
  const id = lang === "id";
  const toggle = async (v) => { try { const r = await api.patch("/me", { marketing_opt_in: v }); setUser(r.data); } catch (e) { toast.error(errMsg(e)); } };
  const savePhone = async () => { try { const r = await api.patch("/me", { phone }); setUser(r.data); setPhone(r.data.phone); toast.success(id ? "Tersimpan" : "Saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        {(rows || []).map((c) => (
          <div key={c.code} className="border border-dashed border-[#8C4F27] px-5 py-4" data-testid={`my-coupon-${c.code}`}>
            <p className="font-mono text-lg tracking-widest text-[#8C4F27]">{c.code}</p>
            <p className="text-xs text-[#6E6259]">{c.type === "percent" ? `${c.value}%` : `Rp ${c.value.toLocaleString("id-ID")}`} · {c.used}/{c.max_uses || "∞"} used</p>
          </div>
        ))}
        {rows && !rows.length && <Empty />}
      </div>
      <label className="flex items-center gap-3 text-sm"><Switch checked={!!user.marketing_opt_in} onCheckedChange={toggle} data-testid="marketing-optin-switch" />{id ? "Terima info produk baru & promo (WhatsApp)" : "Receive product launches & promotions (WhatsApp)"}</label>
      <div className="flex gap-2 max-w-md">
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={id ? "Nomor WhatsApp, mis. 0812..." : "WhatsApp, e.g. 0812..."} data-testid="coupons-phone-input" className="flex-1 border border-[#E6DFD5] bg-white px-3 py-2 text-sm" />
        <button onClick={savePhone} data-testid="profile-phone-save" className="bg-[#3B220C] text-white px-4 text-sm">{id ? "Simpan" : "Save"}</button>
      </div>
    </div>
  );
}

function Profile() {
  const { user, setUser, lang } = useApp();
  const id = lang === "id";
  const [f, setF] = useState({ name: user.name || "", contact_email: user.contact_email || user.email || "", phone: user.phone || "",
    address: user.address || "", city: user.city || "", postal_code: user.postal_code || "" });
  const [busy, setBusy] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { const r = await api.patch("/me", f); setUser(r.data); toast.success(id ? "Data akun tersimpan" : "Account saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const cls = "w-full border border-[#E6DFD5] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#8C4F27]";
  const F = ({ k, label, area, ...p }) => (
    <label className="block space-y-1.5"><span className="text-xs text-[#6E6259]">{label}</span>
      {area ? <textarea rows={3} value={f[k]} onChange={s(k)} className={cls} data-testid={`profile-${k}-input`} {...p} />
        : <input value={f[k]} onChange={s(k)} className={cls} data-testid={`profile-${k}-input`} {...p} />}
    </label>
  );
  return (
    <div className="max-w-2xl space-y-4" data-testid="profile-form">
      <div className="grid sm:grid-cols-2 gap-4">
        {F({ k: "name", label: id ? "Nama lengkap" : "Full name" })}
        {F({ k: "contact_email", label: "Email", type: "email" })}
        {F({ k: "phone", label: id ? "Nomor HP / WhatsApp" : "Phone / WhatsApp", placeholder: "0812..." })}
        {F({ k: "city", label: id ? "Kota" : "City" })}
      </div>
      {F({ k: "address", label: id ? "Alamat lengkap" : "Full address", area: true })}
      <div className="sm:w-1/2">{F({ k: "postal_code", label: id ? "Kode pos" : "Postal code" })}</div>
      <p className="text-xs text-[#6E6259]" data-testid="profile-login-email">{id ? "Akun login" : "Sign-in account"}: {user.email}</p>
      <button onClick={save} disabled={busy} data-testid="profile-save-btn" className="bg-[#3B220C] text-white px-6 py-3 text-sm hover:bg-[#4A2C11] transition-colors disabled:opacity-40">{id ? "Simpan perubahan" : "Save changes"}</button>
    </div>
  );
}

export default function Account() {
  const { t, user } = useApp();
  const [pwOpen, setPwOpen] = useState(false);
  const { tx } = usePwText();
  const { lang } = useApp();
  const tabs = [["profile", lang === "id" ? "Akun saya" : "My account", Profile], ["orders", t("my_orders"), Orders], ["downloads", t("downloads"), Downloads], ["courses", t("my_courses"), Courses],
    ["workshops", t("my_workshops"), Workshops], ["certificates", t("certificates"), Certificates], ["warranty", lang === "id" ? "Keaslian & Garansi" : "Authentication & Warranty", Warranties], ["coupons", t("coupons"), Coupons]];
  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-14" data-testid="account-page">
      <p className="font-mono text-xs tracking-[0.2em] text-[#8C4F27]">{t("account").toUpperCase()}</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-2 mb-10" data-testid="account-name">{user.name}</h1>
      {user.has_password && <button onClick={() => setPwOpen(true)} className="-mt-6 mb-8 text-sm underline" data-testid="open-change-password-btn">{tx.change}</button>}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="rounded-none max-w-md"><DialogTitle className="font-serif text-3xl font-normal">{tx.change}</DialogTitle><ChangePasswordForm onDone={() => setPwOpen(false)} /></DialogContent>
      </Dialog>
      <Tabs defaultValue="profile">
        <TabsList className="bg-transparent border-b border-[#E6DFD5] rounded-none w-full justify-start h-auto p-0 gap-6 overflow-x-auto">
          {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`account-tab-${k}`} className="rounded-none px-0 pb-3 data-[state=active]:shadow-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#3B220C]">{l}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([k, , C]) => <TabsContent key={k} value={k} className="pt-8"><C /></TabsContent>)}
      </Tabs>
    </div>
  );
}
